import { Icon } from "../Icon/Icon.js";
import { Layer } from "../_overlay/Layer.js";
import { useAnchoredPosition } from "../_overlay/useAnchoredPosition.js";
/* empty css            */
import { useEffect, useLayoutEffect } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/_listbox/Listbox.tsx
/**
* The popup list of a combobox (MultiSelect, SearchSelect): a listbox in a dropdown
* layer, anchored under the combobox's box. Focus stays in the combobox, which points at
* the highlighted option with `aria-activedescendant`, so options never take focus and a
* press on one keeps it where it is. Render it only while open.
*/
function ListboxPopup({ id, anchorRef, options, optionId, activeValue, isSelected, multiple = false, emptyMessage, loadingMessage, onSelect, onActiveChange, onDismiss, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy }) {
	const { setAnchor, setFloating, style, side, isPositioned } = useAnchoredPosition({
		open: true,
		side: "bottom",
		align: "start",
		offset: 4,
		matchWidth: true
	});
	useLayoutEffect(() => setAnchor(anchorRef.current), [setAnchor, anchorRef]);
	const activeOption = options.find((option) => option.value === activeValue);
	const activeId = activeOption ? optionId(activeOption) : null;
	useEffect(() => {
		if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: "nearest" });
	}, [activeId]);
	return /* @__PURE__ */ jsxs(Layer, {
		kind: "dropdown",
		ref: setFloating,
		insideRefs: [anchorRef],
		onDismiss,
		className: "ds-listbox-popup",
		"data-side": side,
		style: {
			...style,
			visibility: isPositioned ? void 0 : "hidden"
		},
		children: [/* @__PURE__ */ jsx("div", {
			id,
			role: "listbox",
			"aria-multiselectable": multiple || void 0,
			"aria-label": ariaLabel,
			"aria-labelledby": ariaLabel ? void 0 : ariaLabelledBy,
			className: "ds-listbox",
			children: loadingMessage == null && options.map((option) => {
				const selected = isSelected(option.value);
				return /* @__PURE__ */ jsxs("div", {
					id: optionId(option),
					role: "option",
					"aria-selected": selected,
					"aria-disabled": option.disabled || void 0,
					"data-active": option.value === activeValue || void 0,
					className: "ds-listbox__option",
					onMouseDown: (event) => event.preventDefault(),
					onMouseMove: () => {
						if (!option.disabled && option.value !== activeValue) onActiveChange(option.value);
					},
					onClick: () => {
						if (!option.disabled) onSelect(option);
					},
					children: [
						multiple && /* @__PURE__ */ jsx("span", {
							className: "ds-listbox__check",
							"aria-hidden": "true",
							children: /* @__PURE__ */ jsx(Icon, {
								name: "check",
								className: "ds-listbox__mark"
							})
						}),
						/* @__PURE__ */ jsxs("span", {
							className: "ds-listbox__text",
							children: [/* @__PURE__ */ jsx("span", {
								className: "ds-listbox__label",
								children: option.label
							}), option.description && /* @__PURE__ */ jsx("span", {
								className: "ds-listbox__description",
								children: option.description
							})]
						}),
						!multiple && selected && /* @__PURE__ */ jsx(Icon, {
							name: "check",
							className: "ds-listbox__selected-mark"
						})
					]
				}, option.value);
			})
		}), loadingMessage != null ? /* @__PURE__ */ jsx("p", {
			className: "ds-listbox__message",
			children: loadingMessage
		}) : options.length === 0 && /* @__PURE__ */ jsx("p", {
			className: "ds-listbox__message",
			children: emptyMessage
		})]
	});
}
/**
* The next option to highlight when an arrow key moves `step` through the enabled
* options: from none, the first (down) or last (up); it stops at the ends.
*/
function nextActive(enabled, active, step) {
	if (enabled.length === 0) return null;
	const at = enabled.findIndex((option) => option.value === active);
	const next = at === -1 ? step === 1 ? 0 : enabled.length - 1 : at + step;
	return enabled[Math.min(Math.max(next, 0), enabled.length - 1)].value;
}
/** Filters options by label (and description), anywhere, ignoring case. */
function filterOptions(options, query) {
	const q = query.trim().toLowerCase();
	if (!q) return options;
	return options.filter((option) => option.label.toLowerCase().includes(q) || option.description?.toLowerCase().includes(q));
}
//#endregion
export { ListboxPopup, filterOptions, nextActive };

//# sourceMappingURL=Listbox.js.map